import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export type DataMode = 'demo' | 'live';
export type ScreenName = 'overview' | 'calendar' | 'resources' | 'settings';

interface ScreenDataModes {
  overview: DataMode;
  calendar: DataMode;
  resources: DataMode;
  settings: DataMode;
}

interface DataModeContextType {
  screenModes: ScreenDataModes;
  getScreenMode: (screen: ScreenName) => DataMode;
  setScreenMode: (screen: ScreenName, mode: DataMode) => void;
  toggleScreenMode: (screen: ScreenName) => void;
  isDemo: (screen: ScreenName) => boolean;
  isLive: (screen: ScreenName) => boolean;
}

const STORAGE_KEY = 'eb-scheduler-data-modes';

const defaultModes: ScreenDataModes = {
  overview: 'demo',
  calendar: 'demo',
  resources: 'demo',
  settings: 'demo',
};

const GLOBAL_MODE_KEY = 'eb-scheduler-global-data-mode';

function loadGlobalMode(): DataMode {
  try {
    const stored = localStorage.getItem(GLOBAL_MODE_KEY);
    if (stored === 'live' || stored === 'demo') return stored;
  } catch (error) {
    console.error('Error loading global data mode from storage:', error);
  }
  return 'demo';
}

const DataModeContext = createContext<DataModeContextType | undefined>(undefined);

function loadModesFromStorage(): ScreenDataModes {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        overview: parsed.overview || defaultModes.overview,
        calendar: parsed.calendar || defaultModes.calendar,
        resources: parsed.resources || defaultModes.resources,
        settings: parsed.settings || defaultModes.settings,
      };
    }
  } catch (error) {
    console.error('Error loading data modes from storage:', error);
  }
  return defaultModes;
}

function saveModesToStorage(modes: ScreenDataModes): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(modes));
  } catch (error) {
    console.error('Error saving data modes to storage:', error);
  }
}

export function DataModeProvider({ children }: { children: ReactNode }) {
  const [globalMode, setGlobalMode] = useState<DataMode>(loadGlobalMode);
  const [screenModes, setScreenModes] = useState<ScreenDataModes>(loadModesFromStorage);

  useEffect(() => {
    saveModesToStorage(screenModes);
  }, [screenModes]);

  useEffect(() => {
    try {
      localStorage.setItem(GLOBAL_MODE_KEY, globalMode);
    } catch (error) {
      console.error('Error saving global data mode:', error);
    }
    setScreenModes({
      overview: globalMode,
      calendar: globalMode,
      resources: globalMode,
      settings: globalMode,
    });
  }, [globalMode]);

  const getScreenMode = useCallback((_screen: ScreenName): DataMode => {
    return globalMode;
  }, [globalMode]);

  const setScreenMode = useCallback((_screen: ScreenName, mode: DataMode) => {
    setGlobalMode(mode);
  }, []);

  const toggleScreenMode = useCallback((_screen: ScreenName) => {
    setGlobalMode(prev => prev === 'demo' ? 'live' : 'demo');
  }, []);

  const isDemo = useCallback((_screen: ScreenName): boolean => {
    return globalMode === 'demo';
  }, [globalMode]);

  const isLive = useCallback((_screen: ScreenName): boolean => {
    return globalMode === 'live';
  }, [globalMode]);

  const value: DataModeContextType = {
    screenModes,
    getScreenMode,
    setScreenMode,
    toggleScreenMode,
    isDemo,
    isLive,
  };

  return (
    <DataModeContext.Provider value={value}>
      {children}
    </DataModeContext.Provider>
  );
}

export function useDataMode() {
  const context = useContext(DataModeContext);
  if (context === undefined) {
    throw new Error('useDataMode must be used within a DataModeProvider');
  }
  return context;
}
